import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { AirportShuttle, DirectionsBus, DirectionsCar } from '@mui/icons-material';
import { ruteroMeetingPointText } from '@bandido/shared/types';
import { PickupVerificationNotice } from './PickupVerificationNotice';

import {
  TransferOffer,
  TransferRequestedRoutes,
  transferIcon,
  transferOfferMeetingPointViews,
  transferOfferLegViews,
  transferOfferCoverage,
  transferOperatingHoursViews,
  transferPickupFacts,
  transferRoutePresentation,
  transferTitle,
} from '../transfers';
import { useAudience } from '../audience';
import { PriceBlock } from './PriceBlock';
import { formatMoney } from '../money';
import { formatProviderDateTime } from '../format';
import { MetaLine } from './MetaLine';
import { ResultMedia } from './ResultMedia';
import { RoutePair } from './RoutePair';
import { StatusFlag } from './StatusFlag';
import { RuteroResultCard } from './RuteroResultCard';
import { TransferOperatingHoursSection } from './TransferOperatingHours';

interface Props {
  offer: TransferOffer;
  /**
   * Las horas que se PIDIERON en la búsqueda que produjo esta oferta, por
   * tramo. Sirven para decir "recogida 06:00, pediste 10:00" — que es el dato
   * que evita vender un traslado que sale cuatro horas antes de lo que el
   * asesor cree.
   *
   * Viene del payload COMPROMETIDO de la búsqueda (§21.23.4), nunca del
   * formulario en curso: con `keepPreviousData` el form puede haber cambiado
   * mientras estas ofertas siguen en pantalla, y compararlas contra él
   * marcaría diferencias que no existen.
   */
  requestedTimes?: { outbound?: string; return?: string };
  /**
   * Extremos del pedido comprometido. Solo se muestran cuando la oferta no
   * publica ambos puntos resueltos; nunca se mezclan con un extremo resuelto.
   */
  requestedRoutes?: TransferRequestedRoutes;
  bookingDisabled?: boolean;
  isRoundTrip?: boolean;
  onBook: (offer: TransferOffer) => void;
  /** Posición en la lista de resultados: da el escalonado de la entrada. */
  index?: number;
}

const ICONS = {
  bus: DirectionsBus,
  van: AirportShuttle,
  car: DirectionsCar,
} as const;

/**
 * Una oferta de traslado, y la referencia del arquetipo de PRODUCTO: foto,
 * título del producto, par origen–destino y una línea de hechos quieta.
 *
 * Los dos proveedores describen el servicio distinto (uno en prosa, otro
 * estructurado) pero la card es la MISMA: sin eso, comparar precios en un
 * fan-out se vuelve imposible.
 */
export function TransferOfferCard({
  offer,
  bookingDisabled,
  isRoundTrip = false,
  onBook,
  index,
  requestedTimes,
  requestedRoutes,
}: Props) {
  const { t } = useTranslation();
  const { isAgent } = useAudience();
  const [showConditions, setShowConditions] = useState(false);
  const [showMeetingDetails, setShowMeetingDetails] = useState(false);

  const route = transferRoutePresentation(offer, requestedRoutes);
  const { title, category } = transferTitle(offer);
  const Icon = ICONS[transferIcon(offer)];
  const meetingPoints = transferOfferMeetingPointViews(offer);
  const operatingHours = transferOperatingHoursViews(offer.operatingHours);
  const coverage = transferOfferCoverage({ isRoundTrip, legs: offer.legs });
  const incompleteRoundTrip = coverage !== 'complete';

  /**
   * A qué hora pasa a buscar al pasajero, por tramo — lo que el PROVEEDOR
   * resolvió, no lo que se pidió. Con hotelbeds e ineedtours la hora que se
   * manda en la búsqueda es la del VUELO y ellos calculan la recogida hacia
   * atrás (medido: hasta 4 h antes); con transferz la resuelta es la pedida.
   * Sin esta línea las tres ofertas se ven iguales y describen servicios
   * distintos.
   *
   * TODA la decisión —qué se dice, de qué tramo, y si el respaldo plano
   * corresponde— vive en `transferPickupFacts` (pura, testeada); acá solo se
   * elige la clave de i18n (§21.9).
   */
  const pickupFacts: (string | JSX.Element)[] = transferPickupFacts(
    offer,
    requestedTimes,
  ).map((fact) => {
    if (fact.kind === 'legacy') {
      return t('travelServices.transfers.pickUpAt', { time: fact.time });
    }
    const leg = fact.direction
      ? t(
        fact.direction === 'return'
          ? 'travelServices.booking.return'
          : 'travelServices.booking.outbound',
      )
      : undefined;
    if (fact.kind === 'reconfirm') {
      // El contrato identifica la antelación y el link como el AVISO operativo
      // que hay que mostrar antes de reservar: sin ellos el asesor vende un
      // traslado sin saber que hay que reconfirmar la hora. Ninguno se
      // inventa cuando el proveedor no lo manda.
      const { minHoursBefore, url } = fact;
      const label =
        minHoursBefore !== undefined
          ? t(
            leg
              ? 'travelServices.transfers.pickUpLegReconfirmWithin'
              : 'travelServices.transfers.pickUpReconfirmWithin',
            { leg, hours: minHoursBefore },
          )
          : t(
            leg
              ? 'travelServices.transfers.pickUpLegReconfirm'
              : 'travelServices.transfers.pickUpReconfirm',
            { leg },
          );
      if (!url) return label;
      return (
        <Box component="span" key={`reconfirm-${fact.direction ?? 'single'}`}>
          {`${label} · `}
          <Link href={url} target="_blank" rel="noopener noreferrer" variant="inherit">
            {t('travelServices.transfers.pickUpReconfirmLink')}
          </Link>
        </Box>
      );
    }
    const { time, requested } = fact;
    if (leg) {
      return requested
        ? t('travelServices.transfers.pickUpLegDiffers', { leg, time, requested })
        : t('travelServices.transfers.pickUpLeg', { leg, time });
    }
    return requested
      ? t('travelServices.transfers.pickUpAtDiffers', { time, requested })
      : t('travelServices.transfers.pickUpAt', { time });
  });

  /** Hechos del servicio: texto quieto, no una fila de chips. */
  const facts = [
    category,
    offer.category !== category ? offer.category : undefined,
    typeof offer.passengerCapacity === 'number'
      ? t('travelServices.transfers.capacity', { count: offer.passengerCapacity })
      : undefined,
    typeof offer.luggageCapacity === 'number'
      ? t('travelServices.transfers.luggage', { count: offer.luggageCapacity })
      : undefined,
    typeof offer.includedWaitingMinutes === 'number'
      ? t('travelServices.transfers.waiting', { count: offer.includedWaitingMinutes })
      : undefined,
    ...pickupFacts,
    // La flexibilidad decide la venta: vive con los hechos del viaje y en
    // verde, no enterrada entre las condiciones del operador.
    typeof offer.freeCancellationUntil === 'string' ? (
      <Box component="span" key="free-cancellation" sx={{ color: 'success.dark', fontWeight: 600 }}>
        {t('travelServices.transfers.freeCancellation', {
          date: formatProviderDateTime(offer.freeCancellationUntil),
        })}
      </Box>
    ) : undefined,
  ];

  return (
    <RuteroResultCard
      index={index}
      media={
        <ResultMedia
          photo={offer.images?.[0]}
          icon={<Icon sx={{ fontSize: 44 }} color="action" />}
          alt={title}
        />
      }
      header={
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {/* El proveedor es PROCEDENCIA, no argumento de venta: texto chico y
              gris, y solo para el asesor. */}
          {isAgent && (
            <Typography component="span" color="text.disabled" sx={{ fontSize: 11.5 }}>
              {offer.provider}
            </Typography>
          )}
          {/* Que el operador tenga que ACEPTAR la reserva se dice en la card,
              no después de reservar. */}
          {typeof offer.instantConfirmation === 'boolean' && (
            <Box ml="auto">
              <StatusFlag
                tone={offer.instantConfirmation ? 'ok' : 'warn'}
                label={t(
                  offer.instantConfirmation
                    ? 'travelServices.transfers.instant'
                    : 'travelServices.transfers.onRequest',
                )}
              />
            </Box>
          )}
        </Stack>
      }
      title={
        <Typography sx={{ fontSize: 15.5, fontWeight: 600 }} noWrap title={title}>
          {title}
        </Typography>
      }
      subtitle={
        route ? (
          <Stack spacing={0.25}>
            <RoutePair from={route.from} to={route.to} />
            {route.source !== 'resolved' && (
              <Typography variant="caption" color="text.secondary">
                {t(
                  route.source === 'requested'
                    ? 'travelServices.transfers.requestedRouteHint'
                    : 'travelServices.transfers.resolvedRouteWithoutLabelsHint',
                )}
              </Typography>
            )}
          </Stack>
        ) : undefined
      }
      details={
        <>
          <MetaLine items={facts} />

          {/* Horarios estructurados del servicio. Ausencia no equivale a 24 h
              y una ventana nocturna se muestra exactamente como llegó. */}
          <TransferOperatingHoursSection windows={operatingHours} />

          {/* El punto viene estructurado por tramo. La prosa se deja plegada,
              React la escapa y jamás se interpreta como dirección. */}
          {transferOfferLegViews(offer).filter((leg) => leg.pickup.timeVerification?.required).map((leg) => (
            <Box key={`verification-${leg.direction}`}>
              <Typography variant="caption">{t(leg.direction === 'return' ? 'travelServices.booking.return' : 'travelServices.booking.outbound')}</Typography>
              <PickupVerificationNotice verification={leg.pickup.timeVerification} />
            </Box>
          ))}
          {meetingPoints.length > 0 && (
            <Box mt={0.75}>
              <Link
                component="button"
                type="button"
                variant="caption"
                underline="hover"
                onClick={() => setShowMeetingDetails((prev) => !prev)}
              >
                {t(
                  showMeetingDetails
                    ? 'travelServices.transfers.hideMeetingPoints'
                    : 'travelServices.transfers.showMeetingPoints',
                )}
              </Link>
              {showMeetingDetails && (
                <Stack spacing={0.75} mt={0.5}>
                  {meetingPoints.map(({ direction, meetingPoint }, position) => (
                    <Box key={`${direction}-${position}`}>
                      <Typography variant="caption" fontWeight={600} display="block">
                        {meetingPoints.length > 1
                          ? t(
                            direction === 'return'
                              ? 'travelServices.booking.return'
                              : 'travelServices.booking.outbound',
                          )
                          : t('travelServices.transfers.meetingPoint')}
                      </Typography>
                      {meetingPoint.name && (
                        <Typography variant="caption" display="block">
                          {meetingPoint.name}
                        </Typography>
                      )}
                      {ruteroMeetingPointText(meetingPoint) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}
                        >
                          {ruteroMeetingPointText(meetingPoint)}
                        </Typography>
                      )}
                      {meetingPoint.instructions?.status === 'exceeds_limit' && (
                        <Typography variant="caption" color="warning.dark">{t('travelServices.legs.instructionsUnavailable')}</Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {/* La letra chica del operador (equipaje, cancelación): plegada, que es
              información para transmitir al pasajero, no para la comparación. */}
          {offer.conditions?.length ? (
            <Box mt={0.75}>
              <Link
                component="button"
                type="button"
                variant="caption"
                underline="hover"
                onClick={() => setShowConditions((prev) => !prev)}
              >
                {showConditions
                  ? t('travelServices.transfers.hideConditions')
                  : t('travelServices.transfers.showConditions', {
                    count: offer.conditions.length,
                  })}
              </Link>
              {showConditions && (
                <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
                  {offer.conditions.map((condition, position) => (
                    <Typography
                      key={position}
                      component="li"
                      variant="caption"
                      color="text.secondary"
                    >
                      {condition}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ) : null}
        </>
      }
      price={
        <PriceBlock
          pricing={offer.pricing}
          notes={
            offer.unitPrice &&
            t('travelServices.transfers.perPassenger', {
              price: formatMoney(offer.unitPrice),
            })
          }
        />
      }
      action={
        <Stack spacing={0.5} alignItems="flex-end">
          {incompleteRoundTrip && (
            <Typography variant="caption" color="warning.main" textAlign="right">
              {t(`travelServices.transfers.incompleteRoundTrip.${coverage}`)}
            </Typography>
          )}
          <Button
            variant="contained"
            sx={{ minWidth: 104, height: 30 }}
            disabled={bookingDisabled || incompleteRoundTrip}
            onClick={() => {
              if (!incompleteRoundTrip) onBook(offer);
            }}
          >
            {t(
              incompleteRoundTrip
                ? 'travelServices.transfers.notAvailable'
                : 'travelServices.book',
            )}
          </Button>
        </Stack>
      }
    />
  );
}
